import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Settings, ExternalLink } from 'lucide-react';
import config from '../config';
import { getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';

import { calculateVariation, renderVariation as renderVariationBase } from '../utils/variationUtils';
import { bornesDeLaPeriodeChoisie, calculatePreviousPeriod as bornesDeComparaison } from '../utils/periodUtils';
/**
 * Composant modulaire pour afficher les pages de destination
 * Ce composant charge ses propres données depuis l'endpoint modulaire
 */
const LandingPagesTableModular = React.memo(({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  selectedCampaignFilter,
  token,
  setError,
  onCampaignsLoaded,
  userRole = 'USER',
  compareEnabled = false,
  comparisonType = '',
  moduleId = 'landing_pages'
}) => {
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Configuration des métriques pour ce composant
  const componentMetrics = useComponentMetrics('landing_pages', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // États locaux pour les données
  const [landingPages, setLandingPages] = useState([]);
  const [previousLandingPages, setPreviousLandingPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [allLandingPages, setAllLandingPages] = useState([]); // Stocker toutes les pages non filtrées

  // États pour le tri et la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });

  // Calcul de la période précédente
  const calculatePreviousPeriod = useCallback(() => {
    if (!comparisonType || !selectedPeriod) return null;

    // Les bornes de la période regardée, puis celles auxquelles on la compare. Les deux
    // calculs vivaient ici, recopiés dans une vingtaine d'écrans : ils sont dans
    // `utils/periodUtils`, seul endroit à savoir ce que « hier » veut dire un jour de
    // changement d'heure.
    const plage = bornesDeLaPeriodeChoisie(selectedPeriod, customStartDate, customEndDate);
    if (!plage) return null;

    return bornesDeComparaison(plage.startDate, plage.endDate, comparisonType);
  }, [comparisonType, selectedPeriod, customStartDate, customEndDate]);


  // Affichage de la variation
  const renderVariation = (variation, isInversed = false) => (compareEnabled ? renderVariationBase(variation, isInversed) : null);

  // Map pour accéder rapidement aux données de la période précédente par URL
  const previousLandingPagesMap = useMemo(() => {
    if (!compareEnabled || !previousLandingPages.length) return {};
    const map = {};
    previousLandingPages.forEach(page => {
      if (page.landingPageUrl) {
        map[page.landingPageUrl] = page;
      }
    });
    return map;
  }, [compareEnabled, previousLandingPages]);

  // Totaux de la période précédente
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousLandingPages.length) return null;

    const baseTotals = previousLandingPages.reduce((acc, page) => ({
      impressions: acc.impressions + (page.impressions || 0),
      clicks: acc.clicks + (page.clicks || 0),
      cost: acc.cost + (page.cost || 0),
      conversions: acc.conversions + (page.conversions || 0),
      conversionsValue: acc.conversionsValue + (page.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0,
      roas: baseTotals.cost > 0 ? (baseTotals.conversionsValue / baseTotals.cost * 100) : 0,
      roi: baseTotals.cost > 0 ? ((baseTotals.conversionsValue - baseTotals.cost) / baseTotals.cost) : 0
    };
  }, [compareEnabled, previousLandingPages]);

  // Charger les données quand le composant est monté ou que les dépendances changent
  useEffect(() => {
    if (!selectedAccount || !selectedPeriod || !token) return;

    const loadLandingPages = async () => {
      try {
        setLoading(true);
        console.log('🔗 [LANDING-PAGES-MODULAR] Chargement des pages de destination...');

        const requestBody = {
          sources: [selectedAccount.id],
          period: selectedPeriod,
          campaignFilter: selectedCampaignFilter || null
        };

        // Ajouter les dates personnalisées si période CUSTOM
        if (selectedPeriod === 'CUSTOM') {
          if (!customStartDate || !customEndDate) return;
          requestBody.customStartDate = customStartDate;
          requestBody.customEndDate = customEndDate;
        }

        console.log('🔗 [LANDING-PAGES-MODULAR] Requête vers:', `${config.API_URL}/api/dashboard/components/landing-pages`);
        console.log('🔗 [LANDING-PAGES-MODULAR] Payload:', requestBody);

        const response = await axios.post(
          `${config.API_URL}/api/dashboard/components/landing-pages`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        console.log('🔗 [LANDING-PAGES-MODULAR] Réponse reçue:', response.data);

        if (response.data.success) {
          const pages = response.data.data.landingPages || [];
          setAllLandingPages(pages); // Stocker toutes les pages
          setIsDataLoaded(true);
          console.log('✅ [LANDING-PAGES-MODULAR] Données chargées:', { pages: pages.length });
          
          // Extraire et remonter les campagnes uniques (seulement lors du premier chargement sans filtre)
          if (onCampaignsLoaded && pages.length > 0 && !selectedCampaignFilter) {
            const campaignMap = new Map();
            
            pages.forEach(page => {
              // Extraire depuis le tableau campaigns (structure principale)
              if (page.campaigns && Array.isArray(page.campaigns)) {
                page.campaigns.forEach(campaignName => {
                  if (campaignName && !campaignMap.has(campaignName)) {
                    campaignMap.set(campaignName, {
                      id: campaignName,
                      name: campaignName,
                      campaignId: campaignName
                    });
                  }
                });
              }
            });
            
            const campaigns = Array.from(campaignMap.values());
            console.log('📊 [LANDING-PAGES-MODULAR] Campagnes extraites:', { campaigns: campaigns.length });
            onCampaignsLoaded(campaigns);
          }
        } else {
          console.error('❌ [LANDING-PAGES-MODULAR] API retourne success=false:', response.data);
          setError('Erreur lors du chargement des pages de destination');
        }
      } catch (error) {
        console.error('❌ [LANDING-PAGES-MODULAR] Erreur chargement:', error);
        console.error('❌ [LANDING-PAGES-MODULAR] Détails:', error.response?.data || error.message);
        setError('Erreur lors du chargement des pages de destination');
      } finally {
        setLoading(false);
      }
    };

    loadLandingPages();
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter, token, setError]);

  // Synchroniser landingPages avec allLandingPages (plus de filtrage local nécessaire)
  useEffect(() => {
    setLandingPages(allLandingPages);
  }, [allLandingPages]);

  // Charger les données de la période précédente pour la comparaison
  useEffect(() => {
    const loadPreviousPeriodData = async () => {
      if (!compareEnabled || !comparisonType || !selectedAccount || !token) {
        setPreviousLandingPages([]);
        return;
      }

      const previousDates = calculatePreviousPeriod();
      if (!previousDates) {
        setPreviousLandingPages([]);
        return;
      }

      try {
        const requestBody = {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: previousDates.startDate,
          customEndDate: previousDates.endDate,
          campaignFilter: selectedCampaignFilter || null
        };

        const response = await axios.post(
          `${config.API_URL}/api/dashboard/components/landing-pages`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.success) {
          const pages = response.data.data.landingPages || [];
          setPreviousLandingPages(pages);
          console.log('✅ [LANDING-PAGES-MODULAR] Données période précédente chargées:', { pages: pages.length });
        } else {
          setPreviousLandingPages([]);
        }
      } catch (err) {
        console.error('❌ [LANDING-PAGES-MODULAR] Erreur chargement période précédente:', err);
        setPreviousLandingPages([]);
      }
    };

    loadPreviousPeriodData();
  }, [compareEnabled, comparisonType, selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter, token]);

  // Fonction de tri memoized
  const sortedLandingPages = useMemo(() => {
    if (!landingPages.length) return [];

    const sorted = [...landingPages];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      if (sortConfig.key === 'landingPageUrl') {
        // Pour les URLs, tri alphabétique
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      // Pour les valeurs numériques
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
      
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [landingPages, sortConfig]);

  // Calcul des totaux memoized
  const totals = useMemo(() => {
    if (!landingPages.length) return {};

    const baseTotals = landingPages.reduce((acc, page) => ({
      impressions: acc.impressions + (page.impressions || 0),
      clicks: acc.clicks + (page.clicks || 0),
      cost: acc.cost + (page.cost || 0),
      conversions: acc.conversions + (page.conversions || 0),
      conversionsValue: acc.conversionsValue + (page.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });
    
    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0,
      roas: baseTotals.cost > 0 ? (baseTotals.conversionsValue / baseTotals.cost * 100) : 0,
      roi: baseTotals.cost > 0 ? ((baseTotals.conversionsValue - baseTotals.cost) / baseTotals.cost) : 0
    };
  }, [landingPages]);

  // Configuration de pagination
  const itemsPerPage = 20;
  const totalPages = Math.ceil(sortedLandingPages.length / itemsPerPage);

  // Réinitialiser la page quand les données changent
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Données paginées memoized
  const paginatedLandingPages = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedLandingPages.slice(startIndex, endIndex);
  }, [sortedLandingPages, currentPage, itemsPerPage]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
    setCurrentPage(1); // Réinitialiser à la première page
  }, []);

  // Handler de pagination memoized
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, newPage)));
  }, [totalPages]);

  // Handler de formatage d'URL pour affichage
  const formatUrl = useCallback((url) => {
    if (!url) return '-';
    try {
      const urlObj = new URL(url);
      return urlObj.pathname || '/';
    } catch {
      return url;
    }
  }, []);

  // Ne pas afficher si pas de données et pas en chargement
  if (!loading && !isDataLoaded) return null;

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, sortedLandingPages.length);

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><ExternalLink size={16} />&nbsp;Pages d'atterrissage</h4>
        <div className="section-header-buttons">
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          <button
            onClick={onToggleVisibility}
            className="section-toggle-btn"
            title={isVisible ? "Masquer la section" : "Afficher la section"}
          >
            {isVisible ? '−' : '+'}
          </button>
        </div>
      </div>

      {isVisible && (
        <div className="table-container">
          {loading ? (
            <div className="loading-message" style={{ textAlign: 'center', padding: '40px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 16px auto' }}></div>
              Chargement des pages de destination...
            </div>
          ) : (
            <>
              <div className="campaigns-table-wrapper">
                <table className="campaigns-table" key={componentMetrics.metrics.join(",")} style={{ tableLayout: "auto" }}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('landingPageUrl')} className="sortable">
                        URL de destination {sortConfig.key === 'landingPageUrl' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      {isComponentMetricSelected('impressions') && (
                        <th onClick={() => handleSort('impressions')} className="sortable numeric">
                          Impressions {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('ctr') && (
                        <th onClick={() => handleSort('ctr')} className="sortable numeric">
                          CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('clics') && (
                        <th onClick={() => handleSort('clicks')} className="sortable numeric">
                          Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cpc') && (
                        <th onClick={() => handleSort('cpc')} className="sortable numeric">
                          CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cout') && (
                        <th onClick={() => handleSort('cost')} className="sortable numeric">
                          Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('taux_conv') && (
                        <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                          Taux conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('conv') && (
                        <th onClick={() => handleSort('conversions')} className="sortable numeric">
                          Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cpa') && (
                        <th onClick={() => handleSort('cpa')} className="sortable numeric">
                          CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('valeur_conv') && (
                        <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                          Valeur conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('cos') && (
                        <th onClick={() => handleSort('cos')} className="sortable numeric">
                          COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('roas') && (
                        <th onClick={() => handleSort('roas')} className="sortable numeric">
                          ROAS {sortConfig.key === 'roas' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                      {isComponentMetricSelected('roi') && (
                        <th onClick={() => handleSort('roi')} className="sortable numeric">
                          ROI {sortConfig.key === 'roi' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedLandingPages.length === 0 ? (
                      <tr>
                        <td colSpan="100%" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                          <div>
                            <h4 style={{ margin: '0 0 10px 0', color: '#888' }}>🌐 Pages d'atterrissage non disponibles</h4>
                            <p style={{ margin: '0', fontSize: '0.9em' }}>
                              Aucune donnée de page de destination trouvée pour la période sélectionnée.
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedLandingPages.map((page, index) => {
                        const cos = page.cos || 0;
                        const prevPage = previousLandingPagesMap[page.landingPageUrl];

                        return (
                          <tr key={index}>
                            <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              <a
                                href={page.landingPageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={page.landingPageUrl}
                                style={{ color: '#1a73e8', textDecoration: 'none' }}
                              >
                                {formatUrl(page.landingPageUrl)}
                              </a>
                            </td>
                            {isComponentMetricSelected('impressions') && (
                              <td className="numeric">
                                {formatNumber(page.impressions || 0)}
                                {compareEnabled && prevPage && renderVariation(calculateVariation(page.impressions || 0, prevPage.impressions || 0))}
                              </td>
                            )}
                            {isComponentMetricSelected('ctr') && (
                              <td className="numeric">
                                {formatNumber(page.ctr || 0, 2)}%
                                {compareEnabled && prevPage && renderVariation(calculateVariation(page.ctr || 0, prevPage.ctr || 0))}
                              </td>
                            )}
                            {isComponentMetricSelected('clics') && (
                              <td className="numeric">
                                {formatNumber(page.clicks || 0)}
                                {compareEnabled && prevPage && renderVariation(calculateVariation(page.clicks || 0, prevPage.clicks || 0))}
                              </td>
                            )}
                            {isComponentMetricSelected('cpc') && (
                              <td className="numeric">
                                €{formatNumber(page.cpc || 0, 2)}
                                {compareEnabled && prevPage && renderVariation(calculateVariation(page.cpc || 0, prevPage.cpc || 0), true)}
                              </td>
                            )}
                            {isComponentMetricSelected('cout') && (
                              <td className="numeric">
                                €{formatNumber(page.cost || 0, 2)}
                                {compareEnabled && prevPage && renderVariation(calculateVariation(page.cost || 0, prevPage.cost || 0), true)}
                              </td>
                            )}
                            {isComponentMetricSelected('taux_conv') && (
                              <td className="numeric">
                                {formatNumber(page.conversionRate || 0, 2)}%
                                {compareEnabled && prevPage && renderVariation(calculateVariation(page.conversionRate || 0, prevPage.conversionRate || 0))}
                              </td>
                            )}
                            {isComponentMetricSelected('conv') && (
                              <td className="numeric">
                                {formatNumber(page.conversions || 0, 1)}
                                {compareEnabled && prevPage && renderVariation(calculateVariation(page.conversions || 0, prevPage.conversions || 0))}
                              </td>
                            )}
                            {isComponentMetricSelected('cpa') && (
                              <td className={`numeric ${getCpaClassName(page.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                                €{formatNumber(page.cpa || 0, 2)}
                                {compareEnabled && prevPage && renderVariation(calculateVariation(page.cpa || 0, prevPage.cpa || 0), true)}
                              </td>
                            )}
                            {isComponentMetricSelected('valeur_conv') && (
                              <td className="numeric">
                                €{formatNumber(page.conversionsValue || 0, 2)}
                                {compareEnabled && prevPage && renderVariation(calculateVariation(page.conversionsValue || 0, prevPage.conversionsValue || 0))}
                              </td>
                            )}
                            {isComponentMetricSelected('cos') && (
                              <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', moduleId)}`}>
                                {formatNumber(cos, 1)}%
                                {compareEnabled && prevPage && renderVariation(calculateVariation(cos, prevPage.cos || 0), true)}
                              </td>
                            )}
                            {isComponentMetricSelected('roas') && (
                              <td className="numeric">
                                {formatNumber(page.cost > 0 ? (page.conversionsValue / page.cost * 100) : 0, 1)}%
                                {compareEnabled && prevPage && renderVariation(calculateVariation(
                                  page.cost > 0 ? (page.conversionsValue / page.cost * 100) : 0,
                                  prevPage.cost > 0 ? (prevPage.conversionsValue / prevPage.cost * 100) : 0
                                ))}
                              </td>
                            )}
                            {isComponentMetricSelected('roi') && (
                              <td className="numeric">
                                {formatNumber(page.cost > 0 ? ((page.conversionsValue - page.cost) / page.cost) : 0, 2)}
                                {compareEnabled && prevPage && renderVariation(calculateVariation(
                                  page.cost > 0 ? ((page.conversionsValue - page.cost) / page.cost) : 0,
                                  prevPage.cost > 0 ? ((prevPage.conversionsValue - prevPage.cost) / prevPage.cost) : 0
                                ))}
                              </td>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>

                  {paginatedLandingPages.length > 0 && (
                    <tfoot>
                      <tr className="total-row">
                        <td className="device-name"><strong>TOTAL</strong></td>
                        {isComponentMetricSelected('impressions') && (
                          <td className="numeric">
                            <strong>{formatNumber(totals.impressions)}</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
                          </td>
                        )}
                        {isComponentMetricSelected('ctr') && (
                          <td className="numeric">
                            <strong>{formatNumber(totals.ctr, 2)}%</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.ctr, previousTotals.ctr))}
                          </td>
                        )}
                        {isComponentMetricSelected('clics') && (
                          <td className="numeric">
                            <strong>{formatNumber(totals.clicks)}</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
                          </td>
                        )}
                        {isComponentMetricSelected('cpc') && (
                          <td className="numeric">
                            <strong>€{formatNumber(totals.cpc, 2)}</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.cpc, previousTotals.cpc), true)}
                          </td>
                        )}
                        {isComponentMetricSelected('cout') && (
                          <td className="numeric">
                            <strong>€{formatNumber(totals.cost, 2)}</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost), true)}
                          </td>
                        )}
                        {isComponentMetricSelected('taux_conv') && (
                          <td className="numeric">
                            <strong>{formatNumber(totals.conversionRate, 2)}%</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.conversionRate, previousTotals.conversionRate))}
                          </td>
                        )}
                        {isComponentMetricSelected('conv') && (
                          <td className="numeric">
                            <strong>{formatNumber(totals.conversions, 1)}</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
                          </td>
                        )}
                        {isComponentMetricSelected('cpa') && (
                          <td className={`numeric ${getCpaClassName(totals.cpa, selectedAccount?.id || 'global', moduleId)}`}>
                            <strong>€{formatNumber(totals.cpa, 2)}</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.cpa, previousTotals.cpa), true)}
                          </td>
                        )}
                        {isComponentMetricSelected('valeur_conv') && (
                          <td className="numeric">
                            <strong>€{formatNumber(totals.conversionsValue, 2)}</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
                          </td>
                        )}
                        {isComponentMetricSelected('cos') && (
                          <td className={`numeric ${getCosClassName(totals.cos, selectedAccount?.id || 'global', moduleId)}`}>
                            <strong>{formatNumber(totals.cos, 1)}%</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.cos, previousTotals.cos), true)}
                          </td>
                        )}
                        {isComponentMetricSelected('roas') && (
                          <td className="numeric">
                            <strong>{formatNumber(totals.roas, 1)}%</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.roas, previousTotals.roas))}
                          </td>
                        )}
                        {isComponentMetricSelected('roi') && (
                          <td className="numeric">
                            <strong>{formatNumber(totals.roi, 2)}</strong>
                            {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.roi, previousTotals.roi))}
                          </td>
                        )}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {/* Contrôles de pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>
                      Affichage {startIndex + 1}-{endIndex} sur {sortedLandingPages.length} pages de destination
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button 
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      ← Précédent
                    </button>
                    <span className="page-numbers">
                      Page {currentPage} sur {totalPages}
                    </span>
                    <button 
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="landing_pages"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Pages d'atterrissage"
      />
    </div>
  );
});

LandingPagesTableModular.displayName = 'LandingPagesTableModular';

export default LandingPagesTableModular;